import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  collection, onSnapshot, query, orderBy, where,
  getDocs, updateDoc, deleteDoc, doc, serverTimestamp,
} from "firebase/firestore";

const SESSION_KEY = "baptism_admin_token";
const G = "#c9a55a";
const NAVY = "#12122a";

function StatCard({ label, value, sub }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${G}25` }} className="rounded-2xl p-5 flex flex-col gap-1">
      <span style={{ fontFamily: "'Playfair Display',serif", color: G }} className="text-3xl font-bold">{value}</span>
      <span style={{ color: "#e2e8f0" }} className="text-sm font-medium">{label}</span>
      {sub && <span style={{ color: "#6b6b8a" }} className="text-xs">{sub}</span>}
    </div>
  );
}

function QuickLink({ to, emoji, label, sub }) {
  return (
    <Link to={to}
      style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${G}25` }}
      className="rounded-2xl p-5 flex items-center gap-4 hover:bg-white/[0.07] hover:border-amber-400/40 transition-all group">
      <span className="text-3xl">{emoji}</span>
      <div>
        <p style={{ color: "#e2e8f0", fontFamily: "'Playfair Display',serif" }} className="font-semibold">{label}</p>
        {sub && <p style={{ color: "#6b6b8a" }} className="text-xs mt-0.5">{sub}</p>}
      </div>
      <span style={{ color: G }} className="ml-auto text-lg group-hover:translate-x-1 transition-transform">→</span>
    </Link>
  );
}

export default function BaptismAdmin() {
  const navigate = useNavigate();

  const [quizzes, setQuizzes] = useState([]);
  const [attendeeCounts, setAttendeeCounts] = useState({});
  const [stats, setStats] = useState({ rsvp: 0, players: 0, leaderboard: 0 });

  useEffect(() => {
    if (!document.getElementById("baptism-fonts")) {
      const link = document.createElement("link");
      link.id = "baptism-fonts";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&display=swap";
      document.head.appendChild(link);
    }

    const unsubQuizzes = onSnapshot(
      query(collection(db, "baptism_quizzes"), orderBy("order", "asc")),
      (snap) => setQuizzes(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubAttendees = onSnapshot(
      collection(db, "baptism_quiz_attendees"),
      (snap) => {
        const counts = {};
        snap.docs.forEach((d) => {
          const { quizId } = d.data();
          counts[quizId] = (counts[quizId] || 0) + 1;
        });
        setAttendeeCounts(counts);
      }
    );

    const loadStats = async () => {
      try {
        const [rsvpSnap, lbSnap, attendSnap] = await Promise.all([
          getDocs(query(collection(db, "baptism_rsvps"), where("attending", "==", "yes"))),
          getDocs(collection(db, "baptism_leaderboard")),
          getDocs(collection(db, "baptism_quiz_attendees")),
        ]);
        const uniquePlayers = new Set(attendSnap.docs.map((d) => d.data().name?.toLowerCase())).size;
        setStats({ rsvp: rsvpSnap.size, players: uniquePlayers, leaderboard: lbSnap.size });
      } catch (err) { console.error(err); }
    };
    loadStats();

    return () => { unsubQuizzes(); unsubAttendees(); };
  }, []);

  const logout = () => {
    localStorage.removeItem(SESSION_KEY);
    navigate("/baptism/admin/login", { replace: true });
  };

  const [editingQuizId, setEditingQuizId] = useState(null);
  const [editDraft, setEditDraft] = useState({ title: "", emoji: "", description: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [deletingQuizId, setDeletingQuizId] = useState(null);

  const setQuizStatus = (quizId, status) => {
    const data = { status };
    if (status === "playing") data.startedAt = serverTimestamp();
    return updateDoc(doc(db, "baptism_quizzes", quizId), data);
  };

  const startEdit = (quiz) => {
    setEditingQuizId(quiz.id);
    setEditDraft({ title: quiz.title, emoji: quiz.emoji || "🎯", description: quiz.description || "" });
  };

  const cancelEdit = () => { setEditingQuizId(null); };

  const saveEdit = async (quizId) => {
    if (!editDraft.title.trim()) return;
    setEditSaving(true);
    try {
      await updateDoc(doc(db, "baptism_quizzes", quizId), {
        title: editDraft.title.trim(),
        emoji: editDraft.emoji || "🎯",
        description: editDraft.description.trim(),
      });
      setEditingQuizId(null);
    } finally { setEditSaving(false); }
  };

  const confirmDelete = (quizId) => setDeletingQuizId(quizId);
  const cancelDelete = () => setDeletingQuizId(null);
  const deleteQuiz = async (quizId) => {
    await deleteDoc(doc(db, "baptism_quizzes", quizId));
    setDeletingQuizId(null);
  };

  // ── DASHBOARD ──────────────────────────────────────────────────────────────

  return (
    <div style={{ background: "linear-gradient(160deg,#0d0d1e,#12122a,#0a0a18)", minHeight: "100vh", fontFamily: "'Cormorant Garamond',Georgia,serif" }}>
      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />

      {/* Top bar */}
      <div style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }} className="px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <p style={{ color: `${G}80`, letterSpacing: "0.2em" }} className="text-xs uppercase">Admin Dashboard</p>
            <h1 style={{ fontFamily: "'Playfair Display',serif", color: G }} className="text-xl font-bold leading-tight">
              Munachimso Zoe Maxwell
            </h1>
          </div>
          <button
            onClick={logout}
            style={{ border: `1px solid rgba(255,255,255,0.15)`, color: "#94a3b8" }}
            className="text-sm px-4 py-2 rounded-xl hover:opacity-70 transition-opacity"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-10">

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="RSVP Attending" value={stats.rsvp} sub="confirmed yes" />
          <StatCard label="Quiz Players" value={stats.players} sub="unique players joined" />
          <StatCard label="Leaderboard" value={stats.leaderboard} sub="total entries" />
        </div>

        {/* Quiz management */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 style={{ fontFamily: "'Playfair Display',serif", color: "#f1f5f9" }} className="text-xl font-semibold">
              Quiz Management
            </h2>
            <Link to="/baptism/quiz" style={{ color: G }} className="text-sm hover:opacity-70 transition-opacity">
              + Manage quizzes →
            </Link>
          </div>

          {quizzes.length === 0 ? (
            <div style={{ border: `1px solid ${G}20`, background: "rgba(255,255,255,0.02)" }} className="rounded-2xl p-8 text-center">
              <p style={{ color: "#6b6b8a", fontStyle: "italic" }} className="text-sm">No quizzes yet.</p>
              <Link to="/baptism/quiz" style={{ color: G }} className="text-sm mt-2 inline-block hover:opacity-70">
                Create one →
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {quizzes.map((quiz) => {
                const count = attendeeCounts[quiz.id] || 0;
                const isPlaying = quiz.status === "playing";
                const isWaiting = !quiz.status || quiz.status === "waiting";
                const isEditing = editingQuizId === quiz.id;
                const isConfirmingDelete = deletingQuizId === quiz.id;

                return (
                  <div key={quiz.id} style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${isPlaying ? G : `${G}25`}`, boxShadow: isPlaying ? `0 0 20px ${G}15` : "none" }} className="rounded-2xl p-5">

                    {/* ── Normal view ── */}
                    {!isEditing && !isConfirmingDelete && (
                      <div className="flex flex-wrap items-center gap-4">
                        <span className="text-3xl shrink-0">{quiz.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p style={{ color: "#f1f5f9", fontFamily: "'Playfair Display',serif" }} className="font-semibold">
                              {quiz.title}
                            </p>
                            <span style={{
                              background: isPlaying ? `${G}25` : "rgba(255,255,255,0.06)",
                              color: isPlaying ? G : "#6b6b8a",
                              border: `1px solid ${isPlaying ? G : "rgba(255,255,255,0.1)"}`,
                              letterSpacing: "0.1em",
                            }} className="text-xs px-2.5 py-0.5 rounded-full uppercase">
                              {isPlaying ? "● Live" : "○ Waiting"}
                            </span>
                          </div>
                          <p style={{ color: "#6b6b8a" }} className="text-xs mt-1">
                            {count} player{count !== 1 ? "s" : ""} in lobby
                          </p>
                        </div>

                        <div className="flex gap-2 shrink-0 flex-wrap">
                          {isWaiting ? (
                            <button
                              onClick={() => setQuizStatus(quiz.id, "playing")}
                              style={{ background: `linear-gradient(135deg,#c9a55a,#a07838)` }}
                              className="text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:opacity-90 transition-opacity"
                            >
                              🚀 Start ({count})
                            </button>
                          ) : (
                            <button
                              onClick={() => setQuizStatus(quiz.id, "waiting")}
                              style={{ border: `1px solid ${G}50`, color: G }}
                              className="text-sm px-5 py-2.5 rounded-xl hover:opacity-80 transition-opacity"
                            >
                              ⏸ Stop
                            </button>
                          )}
                          <Link
                            to={`/baptism/quiz/${quiz.id}`}
                            style={{ border: "1px solid rgba(255,255,255,0.12)", color: "#94a3b8" }}
                            className="text-sm px-4 py-2.5 rounded-xl hover:opacity-80 transition-opacity"
                          >
                            Manage →
                          </Link>
                          <button
                            onClick={() => startEdit(quiz)}
                            style={{ border: "1px solid rgba(255,255,255,0.12)", color: "#94a3b8" }}
                            className="text-sm px-3 py-2.5 rounded-xl hover:opacity-80 transition-opacity"
                            title="Edit quiz"
                          >
                            ✏
                          </button>
                          <button
                            onClick={() => confirmDelete(quiz.id)}
                            style={{ border: "1px solid rgba(248,113,113,0.3)", color: "#f87171" }}
                            className="text-sm px-3 py-2.5 rounded-xl hover:opacity-80 transition-opacity"
                            title="Delete quiz"
                          >
                            🗑
                          </button>
                        </div>
                      </div>
                    )}

                    {/* ── Delete confirmation ── */}
                    {isConfirmingDelete && (
                      <div className="flex flex-wrap items-center gap-4">
                        <span className="text-3xl shrink-0">{quiz.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p style={{ color: "#f87171", fontFamily: "'Playfair Display',serif" }} className="font-semibold">Delete "{quiz.title}"?</p>
                          <p style={{ color: "#6b6b8a" }} className="text-xs mt-1">This cannot be undone. Questions will remain but the quiz will be gone.</p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button
                            onClick={() => deleteQuiz(quiz.id)}
                            style={{ background: "rgba(220,38,38,0.8)" }}
                            className="text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:opacity-90 transition-opacity"
                          >
                            Delete
                          </button>
                          <button
                            onClick={cancelDelete}
                            style={{ border: "1px solid rgba(255,255,255,0.12)", color: "#94a3b8" }}
                            className="text-sm px-4 py-2.5 rounded-xl hover:opacity-80 transition-opacity"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* ── Edit form ── */}
                    {isEditing && (
                      <div className="space-y-3">
                        <p style={{ color: `${G}80`, letterSpacing: "0.1em" }} className="text-xs uppercase">Edit Quiz</p>
                        <div className="flex gap-3">
                          <div>
                            <label style={{ color: `${G}70`, letterSpacing: "0.08em" }} className="text-xs uppercase block mb-1">Emoji</label>
                            <input
                              type="text"
                              value={editDraft.emoji}
                              onChange={(e) => setEditDraft((p) => ({ ...p, emoji: e.target.value }))}
                              style={{ borderColor: `${G}40`, background: "rgba(255,255,255,0.06)", color: "#f1f5f9", fontFamily: "'Cormorant Garamond',serif", fontSize: "1.5rem", width: 56, textAlign: "center" }}
                              className="border rounded-xl px-2 py-2 focus:outline-none"
                              onFocus={(e) => (e.target.style.borderColor = G)}
                              onBlur={(e) => (e.target.style.borderColor = `${G}40`)}
                            />
                          </div>
                          <div className="flex-1">
                            <label style={{ color: `${G}70`, letterSpacing: "0.08em" }} className="text-xs uppercase block mb-1">Title</label>
                            <input
                              type="text"
                              value={editDraft.title}
                              onChange={(e) => setEditDraft((p) => ({ ...p, title: e.target.value }))}
                              style={{ borderColor: `${G}40`, background: "rgba(255,255,255,0.06)", color: "#f1f5f9", fontFamily: "'Cormorant Garamond',serif", fontSize: "1rem" }}
                              className="w-full border rounded-xl px-4 py-2.5 focus:outline-none"
                              onFocus={(e) => (e.target.style.borderColor = G)}
                              onBlur={(e) => (e.target.style.borderColor = `${G}40`)}
                            />
                          </div>
                        </div>
                        <div>
                          <label style={{ color: `${G}70`, letterSpacing: "0.08em" }} className="text-xs uppercase block mb-1">Description</label>
                          <input
                            type="text"
                            value={editDraft.description}
                            onChange={(e) => setEditDraft((p) => ({ ...p, description: e.target.value }))}
                            placeholder="Optional description…"
                            style={{ borderColor: `${G}40`, background: "rgba(255,255,255,0.06)", color: "#f1f5f9", fontFamily: "'Cormorant Garamond',serif", fontSize: "0.95rem" }}
                            className="w-full border rounded-xl px-4 py-2.5 focus:outline-none placeholder:text-gray-600"
                            onFocus={(e) => (e.target.style.borderColor = G)}
                            onBlur={(e) => (e.target.style.borderColor = `${G}40`)}
                          />
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={() => saveEdit(quiz.id)}
                            disabled={editSaving || !editDraft.title.trim()}
                            style={{ background: `linear-gradient(135deg,#c9a55a,#a07838)` }}
                            className="text-white text-sm font-semibold px-6 py-2.5 rounded-xl hover:opacity-90 disabled:opacity-50 transition-opacity"
                          >
                            {editSaving ? "Saving…" : "Save"}
                          </button>
                          <button
                            onClick={cancelEdit}
                            style={{ border: "1px solid rgba(255,255,255,0.12)", color: "#94a3b8" }}
                            className="text-sm px-5 py-2.5 rounded-xl hover:opacity-80 transition-opacity"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick links */}
        <div>
          <h2 style={{ fontFamily: "'Playfair Display',serif", color: "#f1f5f9" }} className="text-xl font-semibold mb-4">
            Quick Links
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <QuickLink to="/baptism/mb" emoji="📋" label="RSVP Records" sub="View all guest responses" />
            <QuickLink to="/baptism/leaderboard" emoji="🏆" label="Leaderboard" sub="Scores and rankings" />
            <QuickLink to="/baptism/raffle" emoji="🎟️" label="Raffle Draw" sub="Pick a winner" />
            <QuickLink to="/baptism/quiz" emoji="🎯" label="Quiz Lobby" sub="All quiz games" />
            <QuickLink to="/baptism/gallery" emoji="📸" label="Gallery" sub="Event photos & guest memories" />
          </div>
        </div>

        {/* Footer */}
        <div className="text-center pb-4">
          <Link to="/baptism" style={{ color: `${G}40` }} className="text-xs hover:opacity-70 transition-opacity">
            ← Back to Invite
          </Link>
        </div>

      </div>
      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />
    </div>
  );
}
